<script setup lang="ts">
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import ComboField from '@/components/admin/form/ComboField.vue'
  import DatePickerField from '@/components/admin/form/DatePickerField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { yup } from '@/lib/validation'
  import {
    $Entity,
    type CaseCreateEntityRequest,
    CompanyLegalFormEnum,
    type EntityAugmentedFieldsRequest,
    EntityTypeEnum,
    TitleEnum
  } from '@/types/generated'
  import { Building2, Landmark, RotateCw, User } from 'lucide-vue-next'
  import { computed, defineProps, type PropType, ref, watch } from 'vue'

  const { t } = useTranslation({
    en: {
      client: 'Client Data',
      opponent: 'Opponent Data',
      reset: 'Reset',
      naturalPerson: 'Natural Person',
      legalPerson: 'Legal Person',
      institution: 'Institution',
      contact: 'Contact Person',
      hasInsurance: 'Has Legal Insurance',
      insuranceNumber: 'Insurance Number',
      insuranceName: 'Insurance Company',
      legalInsurance: 'Legal Insurance',
      // Form Fields
      entityType: 'Type',
      title: 'Title',
      specificTitle: 'Specific Title',
      firstName: 'First Name',
      lastName: 'Last Name',
      companyName: 'Company Name',
      companyLegalForm: 'Legal Form',
      institutionName: 'Institution Name',
      phone: 'Phone',
      email: 'Email',
      dateOfBirth: 'Date of Birth',
      street: 'Street',
      streetNumber: 'Street Number',
      addressLine2: 'Address Line 2',
      zip: 'ZIP Code',
      city: 'City'
    },
    de: {
      client: 'Mandant',
      opponent: 'Gegner',
      reset: 'Zurücksetzen',
      naturalPerson: 'Natürliche Person',
      legalPerson: 'Juristische Person',
      institution: 'Institution',
      contact: 'Ansprechpartner',
      hasInsurance: 'Hat Rechtsschutzversicherung',
      insuranceNumber: 'Versicherungsnummer',
      insuranceName: 'Name der Versicherung',
      legalInsurance: 'Rechtsschutzversicherung',
      // Form Fields
      entityType: 'Typ',
      title: 'Anrede',
      specificTitle: 'Titel',
      firstName: 'Vorname',
      lastName: 'Nachname',
      companyName: 'Firmenname',
      companyLegalForm: 'Rechtsform',
      institutionName: 'Name der Institution',
      phone: 'Telefon',
      email: 'E-Mail',
      dateOfBirth: 'Geburtsdatum',
      street: 'Straße',
      streetNumber: 'Hausnummer',
      addressLine2: 'Adresszusatz',
      zip: 'PLZ',
      city: 'Stadt'
    }
  })

  const props = defineProps({
    modelValue: {
      type: Object as PropType<CaseCreateEntityRequest>,
      required: true
    },
    target: {
      type: String as () => 'client' | 'opponent',
      required: false,
      default: 'client'
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    aiFields: {
      type: Object as PropType<EntityAugmentedFieldsRequest>,
      required: false,
      default: () => ({
        entity_type: null,
        company_legal_form: null,
        company_name: null,
        institution_name: null,
        title: null,
        specific_title: null,
        first_name: null,
        last_name: null,
        phone: null,
        email: null,
        date_of_birth: null,
        street: null,
        street_number: null,
        zip: null,
        city: null,
        legal_insurance: null,
        legal_insurance_number: null,
        legal_insurance_name: null
      })
    }
  })

  const entityData = ref<CaseCreateEntityRequest>(props.modelValue)
  const aiFields = computed(() => props.aiFields)
  const invalidFields = ref(new Set<string>())

  const legalInsurance = computed({
    get: () => !!entityData.value.legal_insurance,
    set: (value: boolean) => {
      entityData.value.legal_insurance = value
    }
  })

  const entityTypeOptions = computed(() => [
    {
      value: EntityTypeEnum.NATURAL_PERSON,
      title: t('naturalPerson'),
      icon: User
    },
    {
      value: EntityTypeEnum.LEGAL_PERSON,
      title: t('legalPerson'),
      icon: Building2
    },
    {
      value: EntityTypeEnum.INSTITUTION,
      title: t('institution'),
      icon: Landmark
    }
  ])

  const showClearButton = computed(() => {
    const { entity_type, ...restData } = entityData.value
    return Object.values(restData).some(
      (value) =>
        value !== null &&
        value !== '' &&
        value !== false &&
        (typeof value === 'object'
          ? Object.values(value).some((v) => v !== null && v !== '')
          : true)
    )
  })

  const companyLegalFormOptions = computed(() =>
    Object.entries(CompanyLegalFormEnum).map(([key, value]) => ({
      value,
      title: value
    }))
  )
  const titleOptions = computed(() =>
    Object.entries(TitleEnum).map(([key, value]) => ({
      value,
      title: value
    }))
  )

  const specificTitleOptions = [
    { value: 'Prof.', title: 'Prof.' },
    { value: 'Prof. Dr.', title: 'Prof. Dr.' },
    { value: 'Dr.', title: 'Dr.' },
    { value: 'Dr. Dr.', title: 'Dr. Dr.' }
  ]

  const emits = defineEmits(['update:modelValue', 'update:entityTypeChange', 'invalid', 'clear'])

  const handleInvalid = (fieldName: string, isInvalid: boolean) => {
    if (isInvalid) {
      invalidFields.value.add(fieldName)
    } else {
      invalidFields.value.delete(fieldName)
    }
    const hasErrors = invalidFields.value.size > 0
    emits('invalid', hasErrors)
  }

  const handleChange = (fieldName: keyof EntityAugmentedFieldsRequest) => {
    if (aiFields.value && aiFields.value[fieldName] !== undefined) {
      aiFields.value[fieldName] = null
    }
  }

  const handleEntityTypeChange = (entityType: EntityTypeEnum) => {
    console.log(entityType)
    emits('update:entityTypeChange', entityType)
  }

  const handleClear = (target: 'client' | 'opponent') => {
    emits('clear', target)
  }

  const isAiFilled = (field: keyof EntityAugmentedFieldsRequest) => {
    return aiFields.value?.[field] !== null
  }

  watch(
    () => entityData.value.entity_type,
    () => {
      invalidFields.value.clear()
      emits('invalid', false)
    },
    { immediate: true }
  )

  watch(
    () => props.modelValue,
    (newValue) => {
      entityData.value = newValue
    }
  )

  watch(
    entityData,
    (newValue) => {
      emits('update:modelValue', newValue)
    },
    { deep: true }
  )
</script>

<template>
  <div class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/5">
    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-lg font-medium text-gray-900">
        {{ target === 'client' ? t('client') : t('opponent') }}
      </h2>
      <button
        v-if="showClearButton"
        type="button"
        class="inline-flex h-7 items-center gap-2 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-600
          shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-100 hover:text-gray-700
          focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
          focus-visible:outline-gray-600"
        @click="handleClear(target)"
      >
        <RotateCw class="h-4 w-4" />
        {{ t('reset') }}
      </button>
    </div>
    <SelectField
      v-model="entityData.entity_type"
      name="entityType"
      :disabled="props.disabled"
      :options="entityTypeOptions"
      :label="t('entityType')"
      @update:model-value="handleEntityTypeChange($event)"
    >
      <template #button-contents="{ selectedOption }">
        <div class="flex items-center gap-2">
          <component
            :is="selectedOption?.icon"
            v-if="selectedOption !== null && selectedOption.icon"
            class="size-4"
          />
          <span>{{ selectedOption?.title }}</span>
        </div>
      </template>
    </SelectField>
    <template v-if="entityData.entity_type === EntityTypeEnum.INSTITUTION">
      <div :class="isAiFilled('institution_name') ? 'ai-filled' : ''">
        <InputField
          v-model="entityData.institution_name"
          name="institution_name"
          label="Name der Institution"
          type="text"
          :rules="yup.string().notRequired().max($Entity.properties.institution_name.maxLength)"
          @validation-state="(state) => handleInvalid('institution_name', state.isInvalid)"
        />
      </div>
    </template>
    <template v-else-if="entityData.entity_type === EntityTypeEnum.LEGAL_PERSON">
      <div class="col-span-full mb-2 grid grid-cols-10 gap-4">
        <div
          class="col-span-7"
          :class="isAiFilled('company_name') ? 'ai-filled' : ''"
        >
          <InputField
            v-model="entityData.company_name"
            :disabled="props.disabled"
            name="company_name"
            label="Name der Firma"
            type="text"
            :rules="yup.string().notRequired().max($Entity.properties.company_name.maxLength)"
            @validation-state="(state) => handleInvalid('company_name', state.isInvalid)"
          />
        </div>
        <div
          class="col-span-3"
          :class="isAiFilled('company_legal_form') ? 'ai-filled' : ''"
        >
          <SelectField
            v-model="entityData.company_legal_form"
            name="company_legal_form"
            :label="t('companyLegalForm')"
            :options="companyLegalFormOptions"
            @validation-state="(state) => handleInvalid('company_legal_form', state.isInvalid)"
          />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="col-span-full mb-2 grid grid-cols-2 gap-4">
        <div :class="isAiFilled('title') ? 'ai-filled' : ''">
          <SelectField
            v-model="entityData.title"
            name="title"
            :label="t('title')"
            :options="titleOptions"
            :disabled="props.disabled"
            @validation-state="(state) => handleInvalid('title', state.isInvalid)"
          />
        </div>
        <div :class="isAiFilled('specific_title') ? 'ai-filled' : ''">
          <ComboField
            v-model="entityData.specific_title"
            name="specific_title"
            :label="t('specificTitle')"
            :disabled="props.disabled"
            :options="specificTitleOptions"
            @validation-state="(state) => handleInvalid('specific_title', state.isInvalid)"
            @input="() => handleChange('specific_title')"
          />
        </div>
        <div :class="isAiFilled('date_of_birth') ? 'ai-filled' : ''">
          <DatePickerField
            v-model="entityData.date_of_birth"
            name="date_of_birth"
            :label="t('dateOfBirth')"
            :disabled="props.disabled"
            :rules="yup.string().notRequired()"
            @validation-state="(state) => handleInvalid('date_of_birth', state.isInvalid)"
          />
        </div>
      </div>
      <div class="col-span-full mb-2 grid grid-cols-2 gap-4">
        <div :class="isAiFilled('first_name') ? 'ai-filled' : ''">
          <InputField
            v-model="entityData.first_name"
            :disabled="props.disabled"
            name="first_name"
            :label="t('firstName')"
            type="text"
            :rules="yup.string().notRequired().max($Entity.properties.first_name.maxLength)"
            @validation-state="(state) => handleInvalid('first_name', state.isInvalid)"
            @input="() => handleChange('first_name')"
          />
        </div>
        <div :class="isAiFilled('last_name') ? 'ai-filled' : ''">
          <InputField
            v-model="entityData.last_name"
            :disabled="props.disabled"
            name="last_name"
            :label="t('lastName')"
            type="text"
            :rules="yup.string().notRequired().max($Entity.properties.last_name.maxLength)"
            @validation-state="(state) => handleInvalid('last_name', state.isInvalid)"
            @input="() => handleChange('last_name')"
          />
        </div>
      </div>
      <div class="col-span-full mb-2 grid grid-cols-2 gap-4">
        <div :class="isAiFilled('email') ? 'ai-filled' : ''">
          <InputField
            v-model="entityData.email"
            :disabled="props.disabled"
            name="email"
            :label="t('email')"
            type="email"
            :rules="yup.string().email().notRequired().max($Entity.properties.email.maxLength)"
            @validation-state="(state) => handleInvalid('email', state.isInvalid)"
            @input="() => handleChange('email')"
          />
        </div>
        <div :class="isAiFilled('phone') ? 'ai-filled' : ''">
          <InputField
            v-model="entityData.phone"
            :disabled="props.disabled"
            name="phone"
            :label="t('phone')"
            type="tel"
            :rules="yup.string().notRequired().max($Entity.properties.phone.maxLength)"
            @validation-state="(state) => handleInvalid('phone', state.isInvalid)"
            @input="() => handleChange('phone')"
          />
        </div>
      </div>
    </template>

    <div class="col-span-full mb-2 grid grid-cols-10 gap-4">
      <div
        class="col-span-7"
        :class="isAiFilled('street') ? 'ai-filled' : ''"
      >
        <InputField
          v-model="entityData.street"
          name="street"
          :label="t('street')"
          type="text"
          :disabled="props.disabled"
          :rules="yup.string().notRequired().max($Entity.properties.street.maxLength)"
          @validation-state="(state) => handleInvalid('street', state.isInvalid)"
          @input="() => handleChange('street')"
        />
      </div>
      <div
        class="col-span-3"
        :class="isAiFilled('street_number') ? 'ai-filled' : ''"
      >
        <InputField
          v-model="entityData.street_number"
          name="street_number"
          :label="t('streetNumber')"
          type="text"
          :disabled="props.disabled"
          :rules="yup.string().notRequired().max($Entity.properties.street_number.maxLength)"
          @validation-state="(state) => handleInvalid('street_number', state.isInvalid)"
          @input="() => handleChange('street_number')"
        />
      </div>
    </div>
    <div class="col-span-full mb-2 grid grid-cols-10 gap-4">
      <div
        class="col-span-3"
        :class="isAiFilled('zip') ? 'ai-filled' : ''"
      >
        <InputField
          v-model="entityData.zip"
          name="zip"
          :label="t('zip')"
          type="text"
          :disabled="props.disabled"
          :rules="yup.string().notRequired().max($Entity.properties.zip.maxLength)"
          @validation-state="(state) => handleInvalid('zip', state.isInvalid)"
          @input="() => handleChange('zip')"
        />
      </div>
      <div
        class="col-span-7"
        :class="isAiFilled('city') ? 'ai-filled' : ''"
      >
        <InputField
          v-model="entityData.city"
          name="city"
          :label="t('city')"
          type="text"
          :disabled="props.disabled"
          :rules="yup.string().notRequired().max($Entity.properties.city.maxLength)"
          @validation-state="(state) => handleInvalid('city', state.isInvalid)"
          @input="() => handleChange('city')"
        />
      </div>
    </div>
    <div class="col-span-full mb-2 grid grid-cols-10 gap-4">
      <div
        class="col-span-4"
        :class="isAiFilled('address_line_2') ? 'ai-filled' : ''"
      >
        <InputField
          v-model="entityData.address_line_2"
          name="address_line_2"
          :label="t('addressLine2')"
          type="text"
          :disabled="props.disabled"
          :rules="yup.string().notRequired().max($Entity.properties.address_line_2.maxLength)"
          @validation-state="(state) => handleInvalid('address_line_2', state.isInvalid)"
          @input="() => handleChange('address_line_2')"
        />
      </div>
    </div>
    <template v-if="entityData.entity_type !== EntityTypeEnum.NATURAL_PERSON">
      <div class="mb-2 mt-4 rounded-md border border-gray-100 bg-gray-50 p-3">
        <h3 class="mb-2 text-lg font-semibold">{{ t('contact') }}</h3>
        <div class="mb-2 grid grid-cols-2 gap-4">
          <div :class="isAiFilled('title') ? 'ai-filled' : ''">
            <SelectField
              v-model="entityData.title"
              name="title"
              :label="t('title')"
              :options="titleOptions"
              :disabled="props.disabled"
              :rules="yup.string().notRequired()"
              @validation-state="(state) => handleInvalid('title', state.isInvalid)"
            />
          </div>
          <div :class="isAiFilled('date_of_birth') ? 'ai-filled' : ''">
            <DatePickerField
              v-model="entityData.date_of_birth"
              name="date_of_birth"
              :label="t('dateOfBirth')"
              :disabled="props.disabled"
              :rules="yup.string().notRequired()"
              @validation-state="(state) => handleInvalid('date_of_birth', state.isInvalid)"
            />
          </div>
          <div :class="isAiFilled('first_name') ? 'ai-filled' : ''">
            <InputField
              v-model="entityData.first_name"
              name="first_name"
              :label="t('firstName')"
              type="text"
              :disabled="props.disabled"
              :rules="yup.string().notRequired().max($Entity.properties.first_name.maxLength)"
              @validation-state="(state) => handleInvalid('first_name', state.isInvalid)"
            />
          </div>
          <div :class="isAiFilled('last_name') ? 'ai-filled' : ''">
            <InputField
              v-model="entityData.last_name"
              name="last_name"
              :label="t('lastName')"
              type="text"
              :disabled="props.disabled"
              :rules="yup.string().notRequired().max($Entity.properties.last_name.maxLength)"
              @validation-state="(state) => handleInvalid('last_name', state.isInvalid)"
            />
          </div>
          <div :class="isAiFilled('email') ? 'ai-filled' : ''">
            <InputField
              v-model="entityData.email"
              name="email"
              :label="t('email')"
              type="email"
              :disabled="props.disabled"
              :rules="yup.string().email().notRequired().max($Entity.properties.email.maxLength)"
              @validation-state="(state) => handleInvalid('email', state.isInvalid)"
            />
          </div>
          <div :class="isAiFilled('phone') ? 'ai-filled' : ''">
            <InputField
              v-model="entityData.phone"
              name="phone"
              :label="t('phone')"
              type="tel"
              :disabled="props.disabled"
              :rules="yup.string().notRequired().max($Entity.properties.phone.maxLength)"
              @validation-state="(state) => handleInvalid('phone', state.isInvalid)"
            />
          </div>
        </div>
        <CheckboxField
          v-model="legalInsurance"
          name="legal_insurance"
          :label="t('legalInsurance')"
          :disabled="props.disabled"
        />

        <div :class="isAiFilled('legal_insurance_number') ? 'ai-filled' : ''">
          <InputField
            v-if="legalInsurance"
            v-model="entityData.legal_insurance_number"
            name="legal_insurance_number"
            :label="t('insuranceNumber')"
            type="text"
            :disabled="props.disabled"
            :rules="
              yup.string().notRequired().max($Entity.properties.legal_insurance_number.maxLength)
            "
            @validation-state="(state) => handleInvalid('legal_insurance_number', state.isInvalid)"
            @input="() => handleChange('legal_insurance_number')"
          />
        </div>
        <div :class="isAiFilled('legal_insurance_name') ? 'ai-filled' : ''">
          <InputField
            v-if="legalInsurance"
            v-model="entityData.legal_insurance_name"
            name="legal_insurance_name"
            :label="t('insuranceName')"
            type="text"
            :disabled="props.disabled"
            :rules="
              yup.string().notRequired().max($Entity.properties.legal_insurance_name.maxLength)
            "
            @validation-state="(state) => handleInvalid('legal_insurance_name', state.isInvalid)"
            @input="() => handleChange('legal_insurance_name')"
          />
        </div>
      </div>
    </template>
    <template
      v-if="entityData.entity_type === EntityTypeEnum.NATURAL_PERSON && target === 'client'"
    >
      <div class="col-span-full">
        <CheckboxField
          v-model="legalInsurance"
          name="legal_insurance"
          :label="t('legalInsurance')"
          :disabled="props.disabled"
        />
      </div>
      <div :class="isAiFilled('legal_insurance_name') ? 'ai-filled' : ''">
        <InputField
          v-if="legalInsurance"
          v-model="entityData.legal_insurance_name"
          name="legal_insurance_name"
          :label="t('insuranceName')"
          type="text"
          :disabled="props.disabled"
          :rules="yup.string().notRequired().max($Entity.properties.legal_insurance_name.maxLength)"
          @validation-state="(state) => handleInvalid('legal_insurance_name', state.isInvalid)"
        />
      </div>
      <div
        class="col-span-full"
        :class="isAiFilled('legal_insurance_number') ? 'ai-filled' : ''"
      >
        <InputField
          v-if="legalInsurance"
          v-model="entityData.legal_insurance_number"
          name="legal_insurance_number"
          :label="t('insuranceNumber')"
          type="text"
          :disabled="props.disabled"
          :rules="
            yup.string().notRequired().max($Entity.properties.legal_insurance_number.maxLength)
          "
          @validation-state="(state) => handleInvalid('legal_insurance_number', state.isInvalid)"
        />
      </div>
    </template>
  </div>
</template>

<style>
  .ai-filled label::after {
    content: 'KI';
    background-color: #e9d5ff !important;
    color: #7e22ce;
    margin-left: 4px;
    padding: 2px 4px;
    border-radius: 4px;
    font-size: 0.75rem;
  }
</style>
